/**
 * Subscription Status Utilities
 * 
 * Centralized logic for checking user subscription status.
 * Prioritizes Stripe subscription_status over local booleans.
 */

/**
 * Check if user has active seller access (Gold/Elite subscription)
 * 
 * Priority order:
 * 1. Stripe subscription_status ('active' or 'trialing')
 * 2. subscription_active boolean
 * 3. membership_status ('gold', 'elite', 'Creator')
 * 4. is_seller boolean
 * 5. stripe_subscription_id presence (fallback)
 * 
 * @param {Object} user - User object from auth context
 * @returns {boolean} - True if user has seller access
 */
export const hasSellerAccess = (user) => {
  if (!user) return false;
  
  // Priority 1: Check Stripe subscription_status directly
  // 'active' and 'trialing' both grant full access
  const stripeStatus = user.subscription_status;
  if (stripeStatus === 'active' || stripeStatus === 'trialing') {
    return true;
  }
  
  // Priority 2: Check subscription_active boolean
  if (user.subscription_active === true) {
    return true;
  }
  
  // Priority 3: Check membership_status
  const membershipStatus = user.membership_status;
  if (membershipStatus === 'gold' || membershipStatus === 'elite' || membershipStatus === 'Creator') {
    return true;
  }
  
  // Priority 4: Check is_seller flag
  if (user.is_seller === true) {
    return true;
  }
  
  // Priority 5: Fallback - check if stripe_subscription_id exists
  // This handles edge cases where webhook hasn't updated flags yet
  if (user.stripe_subscription_id) {
    return true;
  }
  
  return false;
};

/**
 * Get user's membership tier display name
 * @param {Object} user - User object
 * @returns {string} - 'Creator', 'Elite', 'Gold', 'Free'
 */
export const getMembershipTier = (user) => {
  if (!user) return 'Free';
  
  if (user.membership_status === 'Creator') return 'Creator';
  if (user.membership_status === 'elite') return 'Elite';
  if (user.membership_status === 'gold') return 'Gold';
  
  // Check if they have active subscription but membership not set
  if (hasSellerAccess(user)) return 'Gold';
  
  return 'Free';
};

/**
 * Check if user is in trial period
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const isTrialing = (user) => {
  return user?.subscription_status === 'trialing';
};

/**
 * Check if user is admin/creator
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const isAdmin = (user) => {
  if (!user) return false;
  
  return (
    user.is_superuser === true ||
    user.is_founder === true ||
    user.role === 'admin' ||
    user.role === 'superuser' ||
    user.membership_status === 'Creator'
  );
};

/**
 * Get subscription status badge info
 * @param {Object} user - User object
 * @returns {{ text: string, color: string }}
 */
export const getSubscriptionBadge = (user) => {
  if (!user) return { text: 'Free', color: 'zinc' };
  
  const status = user.subscription_status;
  
  if (user.membership_status === 'Creator') {
    return { text: 'Creator', color: 'amber' };
  }
  
  if (status === 'trialing') {
    return { text: 'Trial', color: 'emerald' };
  }
  
  if (status === 'active' || hasSellerAccess(user)) {
    const tier = getMembershipTier(user);
    if (tier === 'Elite') return { text: 'Elite', color: 'purple' };
    return { text: 'Gold', color: 'blue' };
  }
  
  if (status === 'past_due') {
    return { text: 'Past Due', color: 'orange' };
  }
  
  if (status === 'canceled') {
    return { text: 'Canceled', color: 'red' };
  }
  
  return { text: 'Free', color: 'zinc' };
};
